import React, { useCallback, useEffect, useMemo, useState } from "react"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import { Button } from "reactstrap"
import { FilterData, IndexType } from "components/Extra"
import CouponsTable from "pages/vendor/Coupons/CouponsTable"
import { CouponsGrid } from "pages/vendor/Coupons/CouponsGrid"
import { AddNewCoupon, PendingApprovalModal } from "pages/vendor/Coupons/Modals"
import { useNavigate } from "react-router-dom"
import { AddNewOffer } from "pages/vendor/Offers/Modals/AddNewOffer/AddNewOffer"
import { AddNewCampaign } from "pages/vendor/Campaigns/Modals/AddNewCampaign"
import { AssignDeliveryManModal } from "pages/vendor/Orders/Modals/AssignDeliveryManModal"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { toast } from "react-toastify"
import { getCoupons, deleteCoupon, updateCoupon } from "store/coupons/actions"
import { getDiscountTypes } from "store/discount-types/actions"

// Backend Coupon type
export type Coupon = {
  id: number
  code: string
  enName: string
  arName: string
  expiryDate: string
  isActive: boolean
  discountValue: number
  discountTypeId: number
  discountTypeName: string
  isExpired: boolean
}

// Mapped coupon type for UI (with missing fields using __ placeholder)
export type UICoupon = {
  id: string
  name: string
  count: number
  price: string
  productSize: string
  refillTimes: string
  itemPerRefill: string
  status: "Active" | "In Active" | "Pending Approval"
  customer: string
}

interface CouponsProps {
  isDashboardPage?: boolean
}

// Map backend coupon to UI coupon (using __ for missing fields)
const mapCouponToUI = (coupon: Coupon): UICoupon => {
  return {
    id: coupon.id.toString(),
    name: coupon.enName || coupon.arName || "__",
    count: 0, // Backend doesn't provide this field
    price: coupon.discountValue?.toString() || "__",
    productSize: "__", // Backend doesn't provide this field
    refillTimes: "__", // Backend doesn't provide this field
    itemPerRefill: "__", // Backend doesn't provide this field
    status: coupon.isExpired
      ? "In Active"
      : coupon.isActive
      ? "Active"
      : "In Active",
    customer: "__", // Backend doesn't provide this field
  }
}

export const Coupons: React.FC<CouponsProps> = ({ isDashboardPage }) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const dispatch = useDispatch()

  // Redux state
  const { coupons, loading, pagination } = useSelector((state: any) => state.Coupons)

  const [activeTab, setActiveTab] = useState<IndexType>("list-ul")
  const [checkedCoupons, setCheckedCoupons] = useState<UICoupon[]>([])
  const [filteredCoupons, setFilteredCoupons] = useState<UICoupon[]>([])
  const [openAddNewCouponModal, setOpenAddNewCouponModal] =
    useState<boolean>(false)
  const [editingCouponId, setEditingCouponId] = useState<number | undefined>(
    undefined
  )
  const [openAddNewOfferModal, setOpenAddNewOfferModal] =
    useState<boolean>(false)
  const [openAddNewCompaignModal, setOpenAddNewCompaignModal] =
    useState<boolean>(false)
  const [openAutoAssignToDeliveryModal, setOpenAutoAssignToDeliveryModal] =
    useState<boolean>(false)
  const [openPendingApprovalModal, setOpenPendingApprovalModal] =
    useState<boolean>(false)
  const [selectedCoupon, setSelectedCoupon] = useState<UICoupon | null>(null)

  // Map backend coupons to UI coupons
  const uiCoupons = useMemo(() => {
    return coupons.map(mapCouponToUI)
  }, [coupons])

  // Fetch coupons on mount
  useEffect(() => {
    dispatch(getCoupons({ PageSize: 10, PageIndex: 0 }, navigate))
    dispatch(getDiscountTypes(navigate))
  }, [dispatch, navigate])

  // Update filtered coupons when uiCoupons changes
  useEffect(() => {
    setFilteredCoupons(uiCoupons)
  }, [uiCoupons])

  const isAllChecked = useMemo(() => {
    return uiCoupons.length > 0 && checkedCoupons.length === uiCoupons.length
  }, [uiCoupons, checkedCoupons])

  const togglecheckedCouponHandler = useCallback((coupon: UICoupon) => {
    setCheckedCoupons(prev =>
      prev.includes(coupon) ? prev.filter(c => c !== coupon) : [...prev, coupon]
    )
  }, [])

  const toogleActiveCoupon = useCallback(
    (coupon: UICoupon) => {
      // If status is Pending Approval, open the pending approval modal
      if (coupon.status === "Pending Approval") {
        setSelectedCoupon(coupon)
        setOpenPendingApprovalModal(true)
        return
      }

      // Find the backend coupon from the store using the ID
      const backendCoupon = coupons.find((c: Coupon) => c.id.toString() === coupon.id)

      if (!backendCoupon) {
        toast.error("Coupon not found")
        return
      }

      // Toggle the isActive field and call the backend API
      const updatedCoupon = {
        isActive: !backendCoupon.isActive,
      }

      dispatch(updateCoupon(backendCoupon.id, updatedCoupon, navigate))
    },
    [coupons, dispatch, navigate]
  )

  const handlePendingApprovalSubmit = useCallback((couponData: any) => {
    // Update the coupon status from "Pending Approval" to "Active"
    if (selectedCoupon) {
      setFilteredCoupons(prev =>
        prev.map(c =>
          c.id === selectedCoupon?.id
            ? {
                ...c,
                status: "Active",
                // Update other properties if needed from the form data
                refillTimes: `${couponData.refillTimesPerWeek} Days/Week`,
                itemPerRefill: `${couponData.itemCountPerOnce} Gallon/Once`,
              }
            : c
        )
      )
    }

    // Close the modal
    setOpenPendingApprovalModal(false)
    setSelectedCoupon(null)
  }, [selectedCoupon])

  const toggleAllCoupons = useCallback(() => {
    setCheckedCoupons(isAllChecked ? [] : uiCoupons)
  }, [isAllChecked, uiCoupons])

  const addNewCouponHandler = useCallback(() => {
    setOpenAddNewCouponModal(true)
  }, [])
  const editCouponHandler = useCallback((coupon: UICoupon) => {
    setEditingCouponId(parseInt(coupon.id))
    setOpenAddNewCouponModal(true)
  }, [])
  const deleteCouponHandler = useCallback((coupon: UICoupon) => {
    // Convert UI coupon ID back to number and dispatch delete action
    const couponId = parseInt(coupon.id)
      dispatch(deleteCoupon(couponId, navigate))
  }, [dispatch, navigate])
  const viewDetailsHandler = useCallback((coupon: UICoupon) => {
    navigate(`/coupons/${coupon.id}`)
  }, [navigate])
  const addOfferHandler = useCallback((coupon: UICoupon) => {
    setOpenAddNewOfferModal(true)
  }, [])
  const addCompaign = useCallback((coupon: UICoupon) => {
    setOpenAddNewCompaignModal(true)
  }, [])
  const autoAssignToDelivery = useCallback((coupon: UICoupon) => {
    setOpenAutoAssignToDeliveryModal(true)
  }, [])

  const deleteCouponsHandler = useCallback(() => {
    if (checkedCoupons.length === 0) {
      toast.info("Please select at least one coupon")
      return
    }

    // Find the backend coupons and update them to inactive
    checkedCoupons.forEach((uiCoupon: UICoupon) => {
      const backendCoupon = coupons.find((c: Coupon) => c.id.toString() === uiCoupon.id)

      if (backendCoupon && backendCoupon.isActive) {
        dispatch(updateCoupon(backendCoupon.id, { isActive: false }, navigate))
      }
    })

    // Clear the selection
    setCheckedCoupons([])
    toast.success(`Making ${checkedCoupons.length} coupon(s) inactive`)
  }, [checkedCoupons, coupons, dispatch, navigate])

  if (isDashboardPage) {
    return (
      <>
        <FilterData
          data={uiCoupons}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          bearerFilteredData={setFilteredCoupons}
          showFilterBtnInsteadOfToggle
        />

        {/* Modals */}
        <AddNewCoupon
          isOpen={openAddNewCouponModal}
          toggle={() => {
            setOpenAddNewCouponModal(false)
            setEditingCouponId(undefined)
          }}
          couponId={editingCouponId}
        />

        <AddNewOffer
          formSubmitedHandler={() => {}}
          isOpen={openAddNewOfferModal}
          toggle={() => setOpenAddNewOfferModal(false)}
        />

        <AddNewCampaign
          isOpen={openAddNewCompaignModal}
          toggle={() => setOpenAddNewCompaignModal(false)}
        />

        <AssignDeliveryManModal
          isOpen={openAutoAssignToDeliveryModal}
          toggle={() => setOpenAutoAssignToDeliveryModal(false)}
          order={null}
          // isAutoAssign={true}
        />

        <PendingApprovalModal
          isOpen={openPendingApprovalModal}
          toggle={() => setOpenPendingApprovalModal(false)}
          coupon={selectedCoupon}
          onSubmit={handlePendingApprovalSubmit}
        />
        <CouponsTable
          coupons={filteredCoupons}
          isAllChecked={isAllChecked}
          checkedCoupons={checkedCoupons}
          toggleAllCoupons={toggleAllCoupons}
          togglecheckedCouponHandler={togglecheckedCouponHandler}
          toogleActiveCoupon={toogleActiveCoupon}
          editCouponHandler={editCouponHandler}
          deleteCouponHandler={deleteCouponHandler}
          viewDetailsHandler={viewDetailsHandler}
          addOfferHandler={addOfferHandler}
          addCompaign={addCompaign}
          autoAssignToDelivery={autoAssignToDelivery}
        />
      </>
    )
  }

  return (
    <div className="page-content">
      <div className="container-fluid d-flex justify-content-between align-items-center">
        <Breadcrumbs
          title={t("dashboard_dashboard")}
          breadcrumbItem={t("coupons_coupons")}
        />
        <div className="d-flex gap-2">
          <Button
            onClick={addNewCouponHandler}
            className="bg-gradiant-blue border-0 text-capitalize"
          >
            {t("coupons_addNewCoupon")}
          </Button>
          <Button
            onClick={deleteCouponsHandler}
            className="bg-transparent border text-danger border-danger text-capitalize"
          >
            {t("coupons_deleteCoupons")}
          </Button>
        </div>
      </div>
      <FilterData
        data={uiCoupons}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        bearerFilteredData={setFilteredCoupons}
      />

      {/* Modals */}
      <AddNewCoupon
        isOpen={openAddNewCouponModal}
        toggle={() => {
          setOpenAddNewCouponModal(false)
          setEditingCouponId(undefined)
        }}
        couponId={editingCouponId}
      />

      <AddNewOffer
        formSubmitedHandler={() => {}}
        isOpen={openAddNewOfferModal}
        toggle={() => setOpenAddNewOfferModal(false)}
      />

      <AddNewCampaign
        isOpen={openAddNewCompaignModal}
        toggle={() => setOpenAddNewCompaignModal(false)}
      />

      <AssignDeliveryManModal
        isOpen={openAutoAssignToDeliveryModal}
        toggle={() => setOpenAutoAssignToDeliveryModal(false)}
        order={null}
        // isAutoAssign={true}
      />

      <PendingApprovalModal
        isOpen={openPendingApprovalModal}
        toggle={() => setOpenPendingApprovalModal(false)}
        coupon={selectedCoupon}
        onSubmit={handlePendingApprovalSubmit}
      />

      {activeTab == "list-ul" ? (
        <CouponsTable
          coupons={filteredCoupons}
          isAllChecked={isAllChecked}
          checkedCoupons={checkedCoupons}
          toggleAllCoupons={toggleAllCoupons}
          togglecheckedCouponHandler={togglecheckedCouponHandler}
          toogleActiveCoupon={toogleActiveCoupon}
          editCouponHandler={editCouponHandler}
          deleteCouponHandler={deleteCouponHandler}
          viewDetailsHandler={viewDetailsHandler}
          addOfferHandler={addOfferHandler}
          addCompaign={addCompaign}
          autoAssignToDelivery={autoAssignToDelivery}
        />
      ) : (
        <CouponsGrid
          coupons={filteredCoupons}
          editCouponHandler={editCouponHandler}
          deleteCouponHandler={deleteCouponHandler}
          viewDetailsHandler={viewDetailsHandler}
          toogleActiveCoupon={toogleActiveCoupon}
          addOfferHandler={addOfferHandler}
          addCompaign={addCompaign}
          autoAssignToDelivery={autoAssignToDelivery}
        />
      )}
    </div>
  )
}
